/**
 * 全局页面水印
 * 用 canvas 生成倾斜文字背景图，铺一层 fixed 全屏 div（pointer-events: none，不挡操作）。
 * 带 MutationObserver，被删掉或改样式会自动还原。
 */

const DEFAULT_OPTIONS = {
  fontSize: 14, // 字号
  fontFamily: 'Microsoft YaHei, PingFang SC, Helvetica Neue, Arial, sans-serif',
  color: 'rgba(0, 0, 0, 0.1)', // 水印颜色，透明度别太高，避免影响阅读
  rotate: -20, // 倾斜角度
  gapX: 200, // 水平间距
  gapY: 100, // 垂直间距
  zIndex: 9999,
}

const CLASS_NAME = 'global-watermark'

let watermarkEl = null
let observer = null
let currText = ''

// 统一成多行数组
const formatLines = (text) => {
  const arr = Array.isArray(text) ? text : [text]
  return arr.filter((item) => item !== null && item !== undefined && item !== '')
}

// 绘制一张水印图，返回 base64 和它在页面上占用的尺寸
function createWatermarkImage(lines, options) {
  const dpr = window.devicePixelRatio || 1
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const font = `${options.fontSize}px ${options.fontFamily}`

  ctx.font = font
  const textWidth = Math.max(...lines.map((line) => ctx.measureText(line).width))
  const lineHeight = options.fontSize * 1.6
  const textHeight = lineHeight * lines.length

  // 旋转后的外接矩形，保证文字不被裁切
  const rad = (options.rotate * Math.PI) / 180
  const width =
    Math.ceil(
      Math.abs(textWidth * Math.cos(rad)) + Math.abs(textHeight * Math.sin(rad))
    ) + options.gapX
  const height =
    Math.ceil(
      Math.abs(textWidth * Math.sin(rad)) + Math.abs(textHeight * Math.cos(rad))
    ) + options.gapY

  canvas.width = width * dpr
  canvas.height = height * dpr
  ctx.scale(dpr, dpr)
  ctx.font = font
  ctx.fillStyle = options.color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.translate(width / 2, height / 2)
  ctx.rotate(rad)
  lines.forEach((line, index) => {
    const offsetY = (index - (lines.length - 1) / 2) * lineHeight
    ctx.fillText(line, 0, offsetY)
  })

  return { url: canvas.toDataURL('image/png'), width, height }
}

// 防删除/防篡改：节点被移除就重新插入，样式被改就还原
function observeWatermark() {
  if (!watermarkEl || !window.MutationObserver) return
  if (observer) {
    observer.disconnect()
  }
  observer = new MutationObserver(() => {
    if (!watermarkEl) return
    if (!document.body.contains(watermarkEl)) {
      document.body.appendChild(watermarkEl)
      return
    }
    if (watermarkEl.className !== CLASS_NAME) {
      watermarkEl.className = CLASS_NAME
    }
    if (watermarkEl.getAttribute('style') !== watermarkEl.__watermarkStyle__) {
      watermarkEl.setAttribute('style', watermarkEl.__watermarkStyle__)
    }
  })
  observer.observe(document.body, { childList: true })
  observer.observe(watermarkEl, { attributes: true, attributeFilter: ['style', 'class'] })
}

/**
 * 设置水印
 * @param {string|string[]} text 水印文字，传数组为多行
 * @param {object} options 覆盖默认配置（fontSize / color / rotate / gapX / gapY / zIndex）
 */
export function setWatermark(text, options = {}) {
  const lines = formatLines(text)
  if (!lines.length) {
    removeWatermark()
    return
  }
  const config = { ...DEFAULT_OPTIONS, ...options }
  const { url, width, height } = createWatermarkImage(lines, config)

  // 已存在则复用节点，只换背景
  if (!watermarkEl) {
    watermarkEl = document.createElement('div')
  }
  const styleText = `position:fixed;left:0;top:0;right:0;bottom:0;pointer-events:none;z-index:${config.zIndex};background-image:url(${url});background-repeat:repeat;background-size:${width}px ${height}px;`
  watermarkEl.className = CLASS_NAME
  watermarkEl.setAttribute('style', styleText)
  // 记录目标样式，供 observer 比对还原
  watermarkEl.__watermarkStyle__ = styleText
  if (!document.body.contains(watermarkEl)) {
    document.body.appendChild(watermarkEl)
  }

  currText = lines.join('|')
  observeWatermark()
}

// 移除水印（退出登录、组件销毁时调用）
export function removeWatermark() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (watermarkEl && watermarkEl.parentNode) {
    watermarkEl.parentNode.removeChild(watermarkEl)
  }
  watermarkEl = null
  currText = ''
}

// 当前水印文字
export function getWatermarkText() {
  return currText
}

export default {
  set: setWatermark,
  remove: removeWatermark,
  getText: getWatermarkText,
}
